import { buildClasses } from "../utils/buildClasses";
import { RyeVariant, RyeVibe, RyeShape } from "./RyeConsts";
import { RyeIcon } from "./RyeIcon";
import { RyeTooltip } from "./RyeTooltip";

export function RyeButton({
  text,
  icon,
  onClick = () => {},
  variant = "outlined",
  vibe = "ordinary",
  size = "md",
  shape = "rounded-rect",
  shadow = "none",
  disabled = false,
  iconColor,
  iconSize,
  iconFill = false,
  primaryColor,
  tooltip,
  tooltipContent,
}: {
  text?: string;
  icon?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  size?: "xs" | "sm" | "md" | "lg";
  shape?: RyeShape;
  shadow?: "none" | "sm" | "md" | "lg";
  variant?: RyeVariant;
  vibe?: RyeVibe;
  disabled?: boolean;
  iconColor?: "red" | "yellow" | "green" | "purple";
  iconSize?: "xs" | "sm" | "md" | "lg" | "xl";
  iconFill?: boolean;
  primaryColor?: "yellow" | "green" | "red" | "purple";
  tooltip?: string;
  /** Formatted tooltip to show in place of `tooltip`, which stays the
   * button's accessible name. */
  tooltipContent?: React.ReactNode;
}): JSX.Element {
  const button = (
    <button
      // Without this, an icon-only button's accessible name is its icon's
      // ligature text (e.g. "tune"), not what the button does.
      aria-label={text === undefined ? tooltip : undefined}
      onClick={(e) => onClick(e)}
      className={buildClasses(
        // Size
        {
          switch: size,
          cases: new Map([
            [
              "xs",
              buildClasses(
                { if: text === undefined, then: "w-5", else: "px-2" },
                "h-5",
                "text-xs",
              ),
            ],
            [
              "sm",
              buildClasses(
                { if: text === undefined, then: "w-7", else: "px-2.5" },
                "h-7",
                "text-sm",
              ),
            ],
            [
              "md",
              buildClasses(
                { if: text === undefined, then: "w-9", else: "px-3" },
                "h-9",
                "text-md",
              ),
            ],
            [
              "lg",
              buildClasses(
                { if: text === undefined, then: "w-11", else: "px-3.5" },
                "h-11",
                "text-lg",
              ),
            ],
          ]),
        },
        // Shape
        {
          switch: shape,
          cases: new Map([
            ["rounded-rect", buildClasses("rounded-md")],
            ["fully-round", buildClasses("rounded-full")],
          ]),
        },
        // Shadow
        {
          switch: shadow,
          cases: new Map([
            ["none", buildClasses("shadow-none")],
            ["sm", buildClasses("shadow-sm")],
            ["md", buildClasses("shadow-md")],
            ["lg", buildClasses("shadow-lg")],
          ]),
        },
        // Variant & Vibe
        primaryColor && vibe === "primary"
          ? getPrimaryColorClasses(primaryColor, variant, vibe)
          : {
              switch: variant,
              cases: new Map([
                [
                  "filled",
                  buildClasses({
                    switch: vibe,
                    cases: new Map([
                      [
                        "subdued",
                        buildClasses(
                          "bg-on-surface-200",
                          "text-on-surface-600",
                          "enabled:hover:bg-on-surface-300",
                        ),
                      ],
                      [
                        "ordinary",
                        buildClasses(
                          "bg-on-surface-400",
                          "text-on-surface-50",
                          "enabled:hover:bg-on-surface-500",
                        ),
                      ],
                      [
                        "primary",
                        buildClasses(
                          "bg-purple",
                          "text-on-surface-50",
                          "enabled:hover:bg-purple-600",
                        ),
                      ],
                      [
                        "danger",
                        buildClasses(
                          "bg-red",
                          "text-on-surface-50",
                          "enabled:hover:bg-red-600",
                        ),
                      ],
                    ]),
                  }),
                ],
                [
                  "outlined",
                  buildClasses({
                    switch: vibe,
                    cases: new Map([
                      [
                        "subdued",
                        buildClasses(
                          "border",
                          "border-on-surface-200",
                          "text-on-surface-500",
                          "enabled:hover:bg-on-surface-100",
                          "enabled:hover:border-on-surface-300",
                          "enabled:hover:text-on-surface-600",
                        ),
                      ],
                      [
                        "ordinary",
                        buildClasses(
                          "border",
                          "border-on-surface-300",
                          "bg-on-surface-100",
                          "text-on-surface-600",
                          "enabled:hover:bg-on-surface-200",
                          "enabled:hover:border-on-surface-400",
                          "enabled:hover:text-on-surface-700",
                        ),
                      ],
                      [
                        "primary",
                        buildClasses(
                          "border",
                          "border-purple",
                          "bg-purple-50",
                          "text-purple",
                          "enabled:hover:bg-purple-100",
                          "enabled:hover:border-purple-600",
                          "enabled:hover:text-purple-600",
                        ),
                      ],
                      [
                        "danger",
                        buildClasses(
                          "border",
                          "border-red",
                          "bg-red-100",
                          "text-red",
                          "enabled:hover:bg-red-200",
                          "enabled:hover:border-red-600",
                          "enabled:hover:text-red-600",
                        ),
                      ],
                    ]),
                  }),
                ],
                [
                  "transparent",
                  buildClasses({
                    switch: vibe,
                    cases: new Map([
                      [
                        "subdued",
                        buildClasses(
                          "text-on-surface-500",
                          "enabled:hover:text-on-surface-700",
                        ),
                      ],
                      [
                        "ordinary",
                        buildClasses(
                          "text-on-surface-700",
                          "enabled:hover:text-on-surface-900",
                        ),
                      ],
                      [
                        "primary",
                        buildClasses(
                          "text-purple",
                          "enabled:hover:text-purple-700",
                        ),
                      ],
                      [
                        "danger",
                        buildClasses("text-red", "enabled:hover:text-red-700"),
                      ],
                    ]),
                  }),
                ],
              ]),
            },
        "flex-shrink-0",
        "block", // we do this because of https://stackoverflow.com/questions/40385945/parent-element-with-height-auto-is-higher-than-highest-child
        "disabled:opacity-50",
        "disabled:z-1", // hack to make disabled buttons (which create their own stacking contexts) not show over things like drop-down menus
      )}
      disabled={disabled}
    >
      <ButtonContent
        text={text}
        icon={icon}
        iconColor={iconColor}
        iconSize={iconSize}
        iconFill={iconFill}
        size={size}
      />
    </button>
  );

  if (tooltipContent || tooltip) {
    return <RyeTooltip text={tooltipContent ?? tooltip}>{button}</RyeTooltip>;
  }
  return button;
}

function getIconColorClass(
  iconColor?: "red" | "yellow" | "green" | "purple",
): string {
  if (!iconColor) return "";

  switch (iconColor) {
    case "red":
      return "text-red-500";
    case "yellow":
      return "text-yellow-500";
    case "green":
      return "text-green-500";
    case "purple":
      return "text-purple-500";
    default:
      return "";
  }
}

function getPrimaryColorClasses(
  primaryColor?: "yellow" | "green" | "red" | "purple",
  variant?: RyeVariant,
  vibe?: RyeVibe,
): string {
  if (!primaryColor || vibe !== "primary") return "";

  const colorMap = {
    yellow: {
      filled: {
        bg: "bg-yellow-500",
        hover: "enabled:hover:bg-yellow-600",
        text: "text-white",
      },
      outlined: {
        border: "border-yellow-500",
        bg: "bg-yellow-50",
        text: "text-yellow-600",
        hover:
          "enabled:hover:bg-yellow-100 enabled:hover:border-yellow-600 enabled:hover:text-yellow-700",
      },
      transparent: {
        text: "text-yellow-600",
        hover: "enabled:hover:text-yellow-700",
      },
    },
    green: {
      filled: {
        bg: "bg-green-500",
        hover: "enabled:hover:bg-green-600",
        text: "text-white",
      },
      outlined: {
        border: "border-green-500",
        bg: "bg-green-50",
        text: "text-green-600",
        hover:
          "enabled:hover:bg-green-100 enabled:hover:border-green-600 enabled:hover:text-green-700",
      },
      transparent: {
        text: "text-green-600",
        hover: "enabled:hover:text-green-700",
      },
    },
    red: {
      filled: {
        bg: "bg-red-500",
        hover: "enabled:hover:bg-red-600",
        text: "text-white",
      },
      outlined: {
        border: "border-red-500",
        bg: "bg-red-50",
        text: "text-red-600",
        hover:
          "enabled:hover:bg-red-100 enabled:hover:border-red-600 enabled:hover:text-red-700",
      },
      transparent: {
        text: "text-red-600",
        hover: "enabled:hover:text-red-700",
      },
    },
    purple: {
      filled: {
        bg: "bg-purple-500",
        hover: "enabled:hover:bg-purple-600",
        text: "text-white",
      },
      outlined: {
        border: "border-purple-500",
        bg: "bg-purple-50",
        text: "text-purple-600",
        hover:
          "enabled:hover:bg-purple-100 enabled:hover:border-purple-600 enabled:hover:text-purple-700",
      },
      transparent: {
        text: "text-purple-600",
        hover: "enabled:hover:text-purple-700",
      },
    },
  };

  const colorConfig = colorMap[primaryColor];
  if (!colorConfig) return "";

  switch (variant) {
    case "filled":
      return buildClasses(
        colorConfig.filled.bg,
        colorConfig.filled.hover,
        colorConfig.filled.text,
      );
    case "outlined":
      return buildClasses(
        "border",
        colorConfig.outlined.border,
        colorConfig.outlined.bg,
        colorConfig.outlined.text,
        colorConfig.outlined.hover,
      );
    case "transparent":
      return buildClasses(
        colorConfig.transparent.text,
        colorConfig.transparent.hover,
      );
    default:
      return "";
  }
}

function ButtonContent({
  text,
  icon,
  iconColor,
  iconSize,
  iconFill,
  size,
}: {
  text?: string;
  icon?: string;
  iconColor?: "red" | "yellow" | "green" | "purple";
  iconSize?: "xs" | "sm" | "md" | "lg" | "xl";
  iconFill?: boolean;
  size: "xs" | "sm" | "md" | "lg";
}): JSX.Element {
  const iconColorClass = getIconColorClass(iconColor);

  if (icon === undefined && text === undefined) {
    return <span></span>;
  }
  if (icon === undefined) {
    return (
      <span className={buildClasses("flex", "items-center", "justify-center")}>
        {text}
      </span>
    );
  }
  const effectiveIconSize = iconSize || size;

  if (text === undefined) {
    return (
      <div className={buildClasses("flex", "items-center", "justify-center")}>
        <RyeIcon
          name={icon}
          size={effectiveIconSize}
          fill={iconFill}
          className={iconColorClass}
        ></RyeIcon>
      </div>
    );
  }
  return (
    <div className={buildClasses("flex", "items-center", "justify-center")}>
      <RyeIcon
        name={icon}
        size={effectiveIconSize}
        fill={iconFill}
        className={buildClasses(
          {
            switch: size,
            cases: new Map([
              ["xs", buildClasses("-ml-1", "mr-0.5")],
              ["sm", buildClasses("-ml-1", "mr-1")],
              ["md", buildClasses("-ml-1", "mr-1.5")],
              ["lg", buildClasses("-ml-1", "mr-2")],
            ]),
          },
          iconColorClass,
        )}
      ></RyeIcon>
      <span>{text}</span>
    </div>
  );
}
